import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { DealerService } from '../../services';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import ChipInput from 'material-ui-chip-input';
import Tooltip from '@material-ui/core/Tooltip';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { useNotification } from '../../contexts/NotificationContext';
import { useError } from '../../contexts/ErrorContext';
import { useRole } from '../../contexts/RolesContext';
import { isSuccessfulResponse } from '../../utils/responseHelpers';

function isBlank(value) {
  return value == null || value.trim().length === 0;
}

function updateRequiredField(value, updater, errorUpdater) {
  if (isBlank(value)) {
    errorUpdater(true);
    updater(value);
    return true;
  } else {
    errorUpdater(false);
    updater(value);
    return false;
  }
}

function updateField(value, updater, errorUpdater) {
  updater(value);
  return false;
}

const useStyles = makeStyles((theme) => ({
  paper: {
      position: 'absolute',
      left: "20%",
      top: "15%",
      width: '60vw',
      height: '75vh',
      backgroundColor: theme.palette.background.paper,
      border: '2px solid #000',
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
      overflowY: 'scroll',
  },
  modalButton: {
      marginLeft: '15px',
      float: 'right',
  },
  coreForm: {
    height: '50vh'
  },
  lockIcons: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  lockedDiv: {
    backgroundColor: 'gainsboro',
    marginTop: '5vh',
    marginBottom: '5vh',
    paddingLeft: '10%',
    paddingRight: '10%',
  },
  unlockedDiv: {
    marginTop: '5vh',
    marginBottom: '5vh',
    paddingLeft: '10%',
    paddingRight: '10%',
  },
}));

const nineDigitRegex = RegExp(/^\d{9}$/);

const UpdateDealer = (props) => {
    const classes = useStyles();
    const [dealerId, setDealerId] = useState(null);
    const [dealerIdError, setDealerIdError] = useState(false);
    const [legalBusinessName, setLegalBusinessName] = useState(null);
    const [legalBusinessNameError, setLegalBusinessNameError] = useState(false);
    const [accountNumber, setAccountNumber] = useState(null);
    const [accountNumberError, setAccountNumberError] = useState(false);
    const [routingNumber, setRoutingNumber] = useState(null);
    const [routingNumberError, setRoutingNumberError] = useState(false);
    const [organization, setOrganization] = useState(null);
    const [affiliate, setAffiliate] = useState(null);
    const [organizationError, setOrganizationError] = useState(false);
    const [affiliateError, setAffiliateError] = useState(false);
    const [notificationEmails, setNotificationEmails] = useState([]);
    const { createErrorNotification } = useNotification();
    const { showError } = useError();
    const { isAchAdmin } = useRole();

    useEffect(() => {
        setDealerId(props.currentDealer?.dealerNumber);
        setLegalBusinessName(props.currentDealer?.legalBusinessName || null);
        setAccountNumber(props.currentDealer?.accountNumber || null);
        setRoutingNumber(props.currentDealer?.routingNumber || null);
        setOrganization(props.currentDealer?.organization || null);
        setAffiliate(props.currentDealer?.affiliate || null);
        setNotificationEmails(props.currentDealer?.notificationEmails || []);
    }, [props.currentDealer])

    const resetState = () => {
      setDealerId(null);
      setDealerIdError(false);
      setLegalBusinessName(null);
      setLegalBusinessNameError(false);
      setAccountNumber(null);
      setAccountNumberError(false);
      setRoutingNumber(null);
      setRoutingNumberError(false);
      setOrganization(null);
      setOrganizationError(false);
      setAffiliate(null);
      setAffiliateError(false);
      setNotificationEmails([]);
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        // First check for missing required fields
        let hasMissingRequiredFields = false;
        hasMissingRequiredFields = updateRequiredField(dealerId, setDealerId, setDealerIdError) || hasMissingRequiredFields;
        hasMissingRequiredFields = updateRequiredField(legalBusinessName, setLegalBusinessName, setLegalBusinessNameError) || hasMissingRequiredFields;
        // Only ACH admins can edit the ACH fields, so only validate them for ACH admins.
        // Otherwise a non-ACH admin editing a dealer with no ACH on file is wedged: the
        // disabled account/routing fields are blank + required, so Save can never succeed.
        if (isAchAdmin()) {
          hasMissingRequiredFields = updateRequiredField(accountNumber, setAccountNumber, setAccountNumberError) || hasMissingRequiredFields;
        }

        if (hasMissingRequiredFields) {
          createErrorNotification("Missing Required Fields");
        } else if (isAchAdmin() && (routingNumberError || isBlank(routingNumber))) {
          createErrorNotification("Invalid Bank Routing number. Must be exactly 9 digits.");
        } else {
            try {
                const response = await DealerService.updateDealer({
                    dealerNumber: dealerId,
                    legalBusinessName: legalBusinessName,
                    organization: organization,
                    notificationEmails: notificationEmails,
                    affiliate: affiliate,
                    ...(isAchAdmin() ? {
                      accountNumber: accountNumber,
                      routingNumber: routingNumber,
                    } : {}),
                });

                if (isSuccessfulResponse(response)) {
                    resetState();
                    props.toggleShowUpdateDealer();
                } else {
                    throw new Error("Failed to save dealer correctly");
                }
            } catch (err) {
                showError(err);
            }
        }
    }

    const handleCancel = () => {
      props.toggleShowUpdateDealer();
      resetState();
    }

    const handleDealerIdChange = (event) => updateRequiredField(event.target.value, setDealerId, setDealerIdError)
    const handleLegalBusinessNameChange = (event) => updateRequiredField(event.target.value, setLegalBusinessName, setLegalBusinessNameError)
    const handleAccountNumberChange = (event) => updateRequiredField(event.target.value, setAccountNumber, setAccountNumberError)
    const handleRoutingNumberChange = (event) => {
      if (nineDigitRegex.test(event.target.value)) {
        setRoutingNumberError(false);
        setRoutingNumber(event.target.value);
      } else {
        setRoutingNumberError(true);
        setRoutingNumber(event.target.value);
      }
    }
    const handleOrganizationChange = (event) => updateField(event.target.value, setOrganization, setOrganizationError);
    const handleAffiliateChange = (event) => updateField(event.target.value, setAffiliate, setAffiliateError);

    const body = (
      <div className={classes.paper}>
          <h2 id="simple-modal-title">Update Dealer</h2>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={5} justifyContent="flex-start">
              <Grid item xs={12}>
                <TextField label="Dealer ID" autoFocus={true} required={true} disabled={true} value={dealerId} error={dealerIdError} onChange={handleDealerIdChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Legal Business Name" fullWidth={true} margin="normal" required={true} value={legalBusinessName} error={legalBusinessNameError} onChange={handleLegalBusinessNameChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Organization" fullWidth={true} margin="normal" value={organization} error={organizationError} onChange={handleOrganizationChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Affiliate" fullWidth={true} margin="normal" value={affiliate} error={affiliateError} onChange={handleAffiliateChange} />
              </Grid>
              <Grid item xs={12}>
                {/* ?TODO: Validate that the input is actually an email? */}
                <ChipInput
                  label="Notification Emails"
                  value={notificationEmails}
                  onAdd={(email) => setNotificationEmails([...notificationEmails, email?.trim()])}
                  onDelete={(email, index) => setNotificationEmails(notificationEmails.filter(val => val !== email))}
                  fullWidth={true}
                  helperText="Emails that will receive funding notifications"
                />
              </Grid>
              <Grid className={isAchAdmin() ? classes.unlockedDiv : classes.lockedDiv} container spacing={5}>
                <Grid className={classes.lockIcons} item xs={1}>
                  <Tooltip title='Only ACH Admins have access to modifying dealer ACH' placement="top">
                    {isAchAdmin() ? <LockOpenIcon fontSize='large'/> : <LockIcon className={classes.lockIcons} fontSize='large'/>}
                  </Tooltip>
                </Grid>
              <Grid item xs={6}>
                <TextField label="Bank Account Number" fullWidth={true} margin="normal" required={true} value={accountNumber} error={accountNumberError} onChange={handleAccountNumberChange} disabled={isAchAdmin() ? false : true}/>
              </Grid>
              <Grid item xs={5}>
                <TextField label="Bank Routing Number" inputProps={{ maxLength: 9 }} fullWidth={true} margin="normal" required={true} value={routingNumber} error={routingNumberError} onChange={handleRoutingNumberChange} disabled={isAchAdmin() ? false : true}/>
              </Grid>
            </Grid>

              <Grid item xs={12}>
                <Button variant="contained" color="primary" className={classes.modalButton} onClick={handleSubmit}>Save</Button>
                <Button variant="contained" className={classes.modalButton} onClick={handleCancel}>Cancel</Button>
              </Grid>
            </Grid>
          </form>
      </div>
  );

  return (
      <div>
          <Modal
          open={props.showUpdateDealer}
          onClose={props.toggleShowUpdateDealer}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          >
              {body}
          </Modal>
      </div>
  );
}

UpdateDealer.propTypes = {
  currentDealer: PropTypes.shape({
    dealerNumber: PropTypes.string,
    legalBusinessName: PropTypes.string,
    accountNumber: PropTypes.string,
    routingNumber: PropTypes.string,
    organization: PropTypes.string,
    affiliate: PropTypes.string,
    notificationEmails: PropTypes.arrayOf(PropTypes.string),
  }),
  showUpdateDealer: PropTypes.bool.isRequired,
  toggleShowUpdateDealer: PropTypes.func.isRequired,
};

export default UpdateDealer;
